import React, { useState } from "react";
import { useStripe } from "@stripe/react-stripe-js";
import ButtonGradient from "../button-gradient";
import TextBase from "../text";
import { Colors } from "../../themes/Colors";
import {
  messageManager,
  MESSAGE_TYPE,
  translate,
} from "../../helper";
import { paymentAPIService } from "../../services/paymentAPIService";
import PersonIcon from '@mui/icons-material/Person';

const ACHBankForm = ({
  goBack = () => { },
  customerEmail = "",
  customerName = "",
  onLinked = () => { },
}) => {
  const stripe = useStripe();

  const [isLinking, setIsLinking] = useState(false);
  const [error, setError] = useState(null);

  const linkBankAccount = async () => {
    setError(null);
    if (!stripe) return;

    setIsLinking(true);

    try {
      const { client_secret } = await paymentAPIService.createAchSetupIntent();

      const fallbackName =
        customerName ||
        (customerEmail ? customerEmail.split("@")[0] : "") ||
        "Customer";

      const collectResult = await stripe.collectBankAccountForSetup({
        clientSecret: client_secret,
        params: {
          payment_method_type: "us_bank_account",
          payment_method_data: {
            billing_details: {
              email: customerEmail || undefined,
              name: fallbackName,
            },
          },
        },
      });

      if (collectResult.error) {
        const msg =
          collectResult.error.message || "Unable to link bank account.";
        setError(msg);
        messageManager({ messageShow: msg, type: MESSAGE_TYPE.error });
        setIsLinking(false);
        return;
      }

      const confirmResult = await stripe.confirmUsBankAccountSetup(
        client_secret
      );

      if (confirmResult.error) {
        const msg = confirmResult.error.message || "Confirmation failed.";
        setError(msg);
        messageManager({ messageShow: msg, type: MESSAGE_TYPE.error });
        setIsLinking(false);
        return;
      }

      const si = confirmResult.setupIntent;

      let successMsg;
      switch (si?.status) {
        case "succeeded":
          successMsg =
            translate("bankLinkedSuccess") ||
            "Bank account linked successfully!";
          break;
        case "processing":
          successMsg =
            translate("bankLinkedProcessing") ||
            "Your bank account is processing verification.";
          break;
        case "requires_action":
          successMsg =
            translate("bankLinkedRequiresAction") ||
            "Further action required to verify your bank account.";
          break;
        default:
          successMsg =
            translate("bankLinkedGenericStatus") ||
            `Bank setup status: ${si?.status || "unknown"}`;
      }

      let bankId = null, fingerprint = null, last4 = null;
      if (si?.payment_method) {
        bankId = si.payment_method;
      }
      if (si?.latest_attempt?.payment_method_details?.us_bank_account) {
        fingerprint = si.latest_attempt.payment_method_details.us_bank_account.fingerprint;
        last4 = si.latest_attempt.payment_method_details.us_bank_account.last4;
      }
      onLinked?.({
        type: "us_bank_account",
        setupIntentStatus: si?.status,
        id: bankId,
        fingerprint,
        last4
      });
      messageManager({ messageShow: successMsg, type: MESSAGE_TYPE.success });
      goBack();
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.message ||
        "Failed to start ACH setup.";
      setError(msg);
      messageManager({ messageShow: msg, type: MESSAGE_TYPE.error });
    } finally {
      setIsLinking(false);
    }
  };

  return (
    <div
      style={{
        paddingBottom: "var(--sp-lg)",
        display: "flex",
        flexDirection: "column",
        gap: "calc(var(--sp-md2)*1.6981132)",
      }}
    >
      <div
        style={{
          background: "linear-gradient(135deg, #eef2ff, #ffffff)",
          padding: "calc(var(--sp-md2)*1.32075)  calc(var(--sp-lg)*1)",
          borderRadius: "calc(var(--sp-md2)*1.509434)",
          display: "flex",
          gap: "calc(var(--sp-md2)*1.132075)",
          alignItems: "center",
          border: "1px solid #e4e7ff",
        }}
      >
        <div
          style={{
            width: "calc(var(--fs-hero)*1.132075)",
            height: "calc(var(--fs-hero)*1.132075)",
            background: "#fff",
            borderRadius: "50%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontSize: "calc(var(--fs-xl)*1.226415)",
            boxShadow: "0 3px 10px rgba(0,0,0,0.05)",
          }}
        >
          🏦
        </div>

        <div>
          <TextBase
            fontWeight={500}
            size={"calc(var(--fs-xs)*1.41509434)"}
            color={Colors.titlePage}
          >
            {translate("achSecureTitle") || "Secure ACH Bank Linking"}
          </TextBase>

          <TextBase
            fontWeight={400}
            size={"calc(var(--fs-xs)*1.226415)"}
            color={Colors.black}
            style={{ marginTop: "calc(var(--sp-xxs)*0.5)" }}
          >
            {translate("instantExpress") ||
              "Instant verification when available"}
          </TextBase>
        </div>
      </div>

      <TextBase
        fontWeight={400}
        size={"calc(var(--fs-xs)*1.32075)"}
        color={Colors.titlePage}
        style={{ lineHeight: "calc(var(--sp-lg)*1.0377)" }}
      >
        {translate("achBankDescription") ||
          "We’ll securely open a Stripe Financial Connections window to link your bank account. You may be required to complete micro-deposit verification."}
      </TextBase>

      {error && (
        <div
          style={{
            padding: "calc(var(--sp-md2)*1.32075) calc(var(--sp-md2)*1.509434)",
            borderRadius: "calc(var(--sp-md2)*1.132075)",
            backgroundColor: "#ffe7e7",
            border: "1px solid #ffcdcd",
          }}
        >
          <TextBase
            fontWeight={500}
            size={"calc(var(--fs-xs)*1.32075)"}
            color={Colors.red}
          >
            {error}
          </TextBase>
        </div>
      )}

      <div
        style={{
          background: "linear-gradient(135deg, #ffffff, #fafbff)",
          border: "1px solid #e6e8f2",
          borderRadius: "calc(var(--sp-md2)*1.509434)",
          padding: "calc(var(--sp-md2)*1.6981132) 20px",
          display: "flex",
          alignItems: "center",
          gap: "calc(var(--sp-md2)*1.32075)",
          boxShadow: "0 4px 14px rgba(0,0,0,0.04)",
        }}
      >
        <div
          style={{
            width: "calc(var(--fs-hero)*1.0849)",
            height: "calc(var(--fs-hero)*1.0849)",
            borderRadius: "50%",
            background: "#eef1ff",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
          }}
        >
          <PersonIcon sx={{color:'#4b4fdd',fontSize:'calc(var(--fs-xl)*1.132075)'}} />
        </div>

        <div style={{ display: "flex", flexDirection: "column" }}>
          <TextBase
            fontWeight={600}
            size={"calc(var(--fs-xs)*1.41509434)"}
            color={Colors.titlePage}
          >
            {customerName || "Customer Name"}
          </TextBase>

          <TextBase
            fontWeight={400}
            size={"calc(var(--fs-xs)*1.226415)"}
            color={Colors.black}
            style={{ marginTop: "calc(var(--sp-xxs)*0.5)" }}
          >
            {customerEmail || "Email not provided"}
          </TextBase>
        </div>
      </div>


      <ButtonGradient
        onClick={linkBankAccount}
        loading={isLinking}
        textStyle={{ fontWeight: 700, fontSize: "var(--fs-md2)" }}
        title={translate("linkBankAccount") || "Link bank account"}
        style={{
          width: "100%",
          padding: "calc(var(--sp-md2)*1.32075) 0",
          borderRadius: "calc(var(--sp-md2)*1.32075)",
          marginTop: "var(--sp-xxs)",
        }}
      />

      <div style={{ marginTop: "var(--sp-sm)", textAlign: "center" }}>
        <TextBase
          fontWeight={400}
          size={"calc(var(--fs-xs)*1.132075)"}
          color={Colors.black}
          style={{ opacity: 0.8 }}
        >
          🔒 Secured by Stripe • Your credentials are never shared with us
        </TextBase>
      </div>
    </div>
  );
};

export default ACHBankForm;